import { ReactNode } from 'react';
import { Navigate } from 'react-router-dom';
import { useFirstAccessiblePath } from '@rsos/hooks/useHasAccess';

type IndexRedirectProps = {
  /**
   * Candidate routes in preference order, as keys into adminRouteAccess
   * (`@rsos/capstone/src/constants/adminRouteAccess`).
   */
  paths: string[];
  /**
   * What to render when the user can reach none of `paths`. Defaults to sending
   * them to the first candidate anyway, so its RequireAccess guard decides where
   * they go rather than this inventing a second answer to the same question.
   */
  fallback?: ReactNode;
};

/**
 * Index route for a path whose children need different permissions: sends the
 * user to the first one they can actually reach.
 *
 * A fixed index target contradicts the descriptors as soon as one admin can reach
 * a section but not its default page — a MANAGE_SECURITY-only admin opening
 * /admin/access would land on Users and be bounced off it, never seeing the
 * Security page they do have.
 *
 * Renders inside the section's SectionGate, so the PSAP that org capabilities are
 * read from has already loaded and capability-gated candidates are answerable.
 */
const IndexRedirect = ({ paths, fallback }: IndexRedirectProps) => {
  const target = useFirstAccessiblePath(paths);

  if (!target) {
    return fallback ? <>{fallback}</> : <Navigate to={paths[0]} replace />;
  }

  return <Navigate to={target} replace />;
};

export default IndexRedirect;
